import { useState } from "react";

import { ideasoftInstallUrl, useIdeasoftStatusQuery } from "@/api/ideasoft";
import type { CredentialKeyInfo } from "@/api/settings";
import { Button, Ribbon } from "@/components/ui";
import { t } from "@/i18n";
import { fmtDate, fmtDateTime } from "@/lib/utils";

const IDEASOFT_FIELDS = [
  { key: "ideasoft_client_id", labelKey: "kurulum.ideasoft.client_id" as const, type: "text" as const },
  { key: "ideasoft_client_secret", labelKey: "kurulum.ideasoft.client_secret" as const, type: "password" as const },
];

const CALLBACK_URL = "https://asakai.vdd-tech.com/api/v2/ideasoft/callback";

/** Backend `/install` ve `/callback` hata durumunda `/kurulum?ideasoft_error=<kod>`'a doner. */
const OAUTH_ERROR_KEYS: Record<string, "kurulum.ideasoft.err.kimlik_bilgisi_eksik" | "kurulum.ideasoft.err.store_url_eksik" | "kurulum.ideasoft.err.state" | "kurulum.ideasoft.err.token" | "kurulum.ideasoft.err.reddedildi" | "kurulum.ideasoft.err.platform"> = {
  kimlik_bilgisi_eksik: "kurulum.ideasoft.err.kimlik_bilgisi_eksik",
  store_url_eksik: "kurulum.ideasoft.err.store_url_eksik",
  gecersiz_state: "kurulum.ideasoft.err.state",
  state_eslesmiyor: "kurulum.ideasoft.err.state",
  tenant_bulunamadi: "kurulum.ideasoft.err.state",
  code_eksik: "kurulum.ideasoft.err.state",
  token_degisimi_basarisiz: "kurulum.ideasoft.err.token",
  access_denied: "kurulum.ideasoft.err.reddedildi",
  platform_uyumsuz: "kurulum.ideasoft.err.platform",
};

/** URL'deki `ideasoft_error` kodunu kullaniciya okunur mesaja cevirir; kod yoksa null. */
function ideasoftOauthErrorMessage(search: string): string | null {
  const code = new URLSearchParams(search).get("ideasoft_error");
  if (!code) return null;
  const key = OAUTH_ERROR_KEYS[code];
  return key ? t(key) : t("kurulum.ideasoft.err.genel", { code });
}

interface IdeasoftConnectProps {
  existing: CredentialKeyInfo[];
  onSave: (values: Record<string, string>) => void;
  isSaving: boolean;
  error: string | null;
  success: boolean;
}

/**
 * IdeaSoft kurulum adimi (gorev talimati B). Uc parca: (1) magaza sahibi
 * IdeaSoft panelinde (Entegrasyonlar → API → Ekle) bir API uygulamasi
 * olusturur — yonlendirme adresi burada kopyalanir; (2) Client ID/Secret
 * TicimaxCredentialsForm ile AYNI desenle kaydedilir (DEGERLER ASLA
 * GORUNTULENMEZ, bos alan GONDERILMEZ — sozlesme §5); (3) "IdeaSoft'ta
 * yetkilendir" OAuth akisini tam sayfa yonlendirmeyle baslatir; (4) baglanti
 * durumu `/api/v2/ideasoft/status`'tan okunur — backend HENUZ YOKSA (404/hata)
 * sessizce "bagli degil" gosterilir, sihirbazi KIRMAZ.
 */
export function IdeasoftConnect({ existing, onSave, isSaving, error, success }: IdeasoftConnectProps) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState(false);
  const [oauthError] = useState<string | null>(() => ideasoftOauthErrorMessage(window.location.search));
  const statusQuery = useIdeasoftStatusQuery();
  const existingByKey = new Map(existing.map((k) => [k.key, k]));

  function handleSubmit() {
    const touched = Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim().length > 0));
    if (Object.keys(touched).length === 0) return;
    onSave(touched);
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(CALLBACK_URL);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Pano erisimi yoksa sessizce yut — adres zaten ekranda, elle secilebilir.
    }
  }

  const connected = statusQuery.data?.connected ?? false;

  return (
    <div className="space-y-4">
      <div className="card space-y-2.5">
        <p className="font-display text-sm font-bold text-ink">{t("kurulum.ideasoft.intro_title")}</p>
        <p className="text-sm text-muted">{t("kurulum.ideasoft.intro_body")}</p>
        <p className="text-xs leading-relaxed text-muted">IdeaSoft’ta API menüsünü yalnız ID=1 ana yönetici hesabı görebilir. Menü görünmüyorsa ana yöneticiyle giriş gerekir. Panelde oturum açmak, AsakAI’nin sürekli veri bağlantısını tek başına kurmaz.</p>
        <div className="flex flex-wrap items-center gap-2">
          <span className="label !mb-0 shrink-0">{t("kurulum.ideasoft.callback_label")}</span>
          <code className="min-w-0 flex-1 truncate rounded-[9px] bg-bg px-2.5 py-1.5 text-xs text-ink">{CALLBACK_URL}</code>
          <Button type="button" variant="outline" size="sm" onClick={() => void handleCopy()}>
            {copied ? t("kurulum.ideasoft.callback_copied") : t("kurulum.ideasoft.callback_copy")}
          </Button>
        </div>
      </div>

      <div className="card space-y-3">
        {error && (
          <p role="alert" className="text-sm text-red">
            {error}
          </p>
        )}
        {success && (
          <p role="status" className="text-sm text-green">
            {t("kurulum.ideasoft.saved")}
          </p>
        )}

        {IDEASOFT_FIELDS.map((field) => {
          const info = existingByKey.get(field.key);
          return (
            <label key={field.key} className="block">
              <span className="label">{t(field.labelKey)}</span>
              <input
                type={field.type}
                autoComplete="off"
                value={values[field.key] ?? ""}
                onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                placeholder={info ? undefined : t("kurulum.ideasoft.not_set")}
                className="input"
              />
              {info && (
                <span className="mt-1 block text-xs text-muted">
                  {t("kurulum.ideasoft.existing", { masked: info.masked, date: fmtDate(info.updated_at) })}
                </span>
              )}
            </label>
          );
        })}

        <Button type="button" variant="primary" disabled={isSaving} onClick={handleSubmit}>
          {t("kurulum.ideasoft.save")}
        </Button>
      </div>

      <div className="card space-y-2.5">
        {oauthError && (
          <Ribbon tone="bad" className="mb-1">
            <p className="text-sm text-ink">{oauthError}</p>
          </Ribbon>
        )}
        {connected ? (
          <>
            <p className="text-sm font-medium text-green">
              {t("kurulum.ideasoft.connected", { store: statusQuery.data?.store_url ?? "" })}
            </p>
            {statusQuery.data?.token_expires_at && (
              <p className="text-xs text-muted">
                {t("kurulum.ideasoft.token_expires_at", { date: fmtDateTime(statusQuery.data.token_expires_at) })}
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-muted">
            {statusQuery.isError ? t("kurulum.ideasoft.status_error") : t("kurulum.ideasoft.not_connected")}
          </p>
        )}
        <Button type="button" variant="primary" onClick={() => window.location.assign(ideasoftInstallUrl())}>
          {t("kurulum.ideasoft.authorize")}
        </Button>
      </div>
    </div>
  );
}
